import { type FC, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Form, type FormInstance, Spin } from 'antd';
import { Select, InputText, Text } from '@g2u/ui-kit';

import { getAbn } from 'queries';
import { type JsonDataItem, type WithOptionalDataTest } from 'types';
import { Toast, addSpacesToNumber, parseErrors } from 'helpers';

type Props = {
  form?: FormInstance;
  name?: string;
  label?: string | ReactNode;
  companyLabel?: string | ReactNode;
  required?: boolean;
  readonly?: boolean;
  defaultAbn?: string;
  defaultName?: string;
  fieldsClassNames?: string;
  disabled?: boolean;
  onSearch?: (numberDetails: AbnLookup.NumberDetails) => void;
} & WithOptionalDataTest;

export const AbnLookup: FC<Props> = (props) => {
  const {
    name,
    label,
    required,
    readonly,
    defaultAbn,
    defaultName,
    form,
    fieldsClassNames,
    companyLabel,
    disabled,
    onSearch,
  } = props;
  const [companyNameList, setCompanyNameList] = useState<string[] | undefined>(defaultName ? [defaultName] : undefined);

  const { mutate: validateAbn, isLoading: isAbnLoading } = useMutation(getAbn, {
    onSuccess: (response) => {
      !response.data.entity_names.includes(defaultName) &&
        form?.setFieldValue('companyName', response.data.entity_names[0]);
      setCompanyNameList(response.data.entity_names);
      onSearch?.(response.data);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      form?.setFields([
        {
          name: 'abn',
          errors: [parseErrors(error).join(', ')],
        },
      ]);
    },
  });
  Form.useWatch(form);

  return (
    <div className={fieldsClassNames} style={{ width: '100%' }}>
      <div style={{ marginBottom: (companyNameList ?? isAbnLoading) ? 8 : 0 }}>
        <InputText.$
          name={name ?? 'abn'}
          required={required}
          rules={[
            {
              min: 14,
              message: 'ABN should have 11 digits',
            },
            { pattern: /^[0-9\s]*$/, message: 'Incorrect ABN format' },
          ]}
          disabled={disabled}
          fullWidth
          placeholder={!disabled ? 'Input' : ''}
          initialValue={defaultAbn === '' ? undefined : defaultAbn}
          maxLength={14}
          readOnly={readonly}
          formatter={(val) => addSpacesToNumber(val?.toString(), [2, 5, 8, 11])}
          dataTestId="abn-field"
          label={
            label ?? <Text.$>ABN: {!required && !disabled && <Text.$ type="secondary">(optional)</Text.$>}</Text.$>
          }
          onChange={(e) => {
            if (e.target.value.length === 14) {
              validateAbn(e.target.value);
            } else {
              setCompanyNameList(undefined);
            }
          }}
        />
      </div>

      {isAbnLoading ? (
        <Spin />
      ) : (
        <>
          {companyNameList?.length === 1 && (
            <InputText.$
              disabled={disabled}
              name="companyName"
              initialValue={companyNameList[0]}
              readonly
              label={companyLabel || 'Company Name'}
              dataTestId="entity-name-field"
              rules={[
                {
                  required: true,
                  message: 'Required field',
                },
              ]}
              fullWidth
            />
          )}

          {companyNameList && companyNameList?.length > 1 && (
            <Select.$
              fullWidth
              name="companyName"
              label={companyLabel || 'Company Name'}
              dataTestId="entity-name-field"
              placeholder="Select"
              initialValue={companyNameList[0]}
              options={companyNameList.map((option) => ({
                key: option,
                label: option,
                value: option,
              }))}
              rules={[
                {
                  required: true,
                  message: 'Required field',
                },
              ]}
            />
          )}
        </>
      )}
    </div>
  );
};

export namespace AbnLookup {
  export type NumberDetails = {
    entity_names: string[];
    gst_status: 'true' | 'false';
    active_status: '1' | '0';
  };
}
